import { useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { api, ApiError } from '@/lib/api';

export function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post('/auth/forgot-password', { email: email.trim() });
      setSent(true);
    } catch (err) {
      // The endpoint always succeeds for valid input; only network/server errors land here.
      toast.error(err instanceof ApiError ? err.message : 'Something went wrong');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex min-h-dvh items-center justify-center bg-aurora p-4">
      <div className="w-full max-w-md">
        <Link to="/" className="mb-6 flex items-center justify-center gap-2 text-lg font-semibold tracking-tight">
          <span className="inline-flex h-7 w-7 items-center justify-center rounded-md bg-primary/15 text-primary">
            <Sparkles className="h-4 w-4" />
          </span>
          BG<span className="text-primary">Swap</span>
        </Link>
        <Card>
          <CardHeader>
            <CardTitle>Forgot password</CardTitle>
            <CardDescription>
              Enter your email and we'll send a reset link. The link expires in 1 hour.
            </CardDescription>
          </CardHeader>
          {sent ? (
            <CardContent className="space-y-3">
              <div className="rounded-md border border-emerald-500/30 bg-emerald-500/10 p-3 text-sm text-emerald-200">
                If an account exists for <strong>{email}</strong>, a reset link is on its way.
                Check your inbox (and spam folder).
              </div>
              <p className="text-xs text-muted-foreground">
                Didn't get it within a few minutes? You can request another link, or ask your
                dealership manager to reset your password from the Team page.
              </p>
              <div className="flex gap-2 pt-2">
                <Button variant="outline" onClick={() => { setSent(false); setEmail(''); }}>
                  Try another email
                </Button>
                <Button asChild className="flex-1">
                  <Link to="/login">Back to sign in</Link>
                </Button>
              </div>
            </CardContent>
          ) : (
            <form onSubmit={onSubmit}>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    autoComplete="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    autoFocus
                  />
                </div>
              </CardContent>
              <CardFooter className="flex flex-col gap-2">
                <Button type="submit" className="w-full" disabled={busy}>
                  {busy ? 'Sending…' : 'Send reset link'}
                </Button>
                <Link to="/login" className="text-xs text-muted-foreground hover:text-foreground">
                  ← Back to sign in
                </Link>
              </CardFooter>
            </form>
          )}
        </Card>
      </div>
    </div>
  );
}
